import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  Grid,
  Group,
  LoadingOverlay,
  RingProgress,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { BarChart, ChartTooltip, DonutChart, LineChart } from "@mantine/charts";
import dayjs from "dayjs";
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconDeviceMobile,
  IconDownload,
  IconHourglassEmpty,
  IconRefresh,
  IconUserExclamation,
  IconUsers,
} from "@tabler/icons-react";
import { toast } from "react-toastify";
import {
  AuditSummary,
  DashboardSummary,
  DistributionDimension,
  DistributionPoint,
  HourlyProductionPoint,
  InjectionResult,
  ModelStatus,
  ProductionTrendPoint,
  TrendUnit,
  WeekdayHourCount,
  WorkerPerformance,
  fetchAuditSummary,
  fetchDashboardSummary,
  fetchDistribution,
  fetchHourlyProduction,
  fetchInjectionResult,
  fetchModelStatus,
  fetchProductionTrend,
  fetchVanCodes,
  fetchWeekdayHourHeatmap,
  fetchWorkerPerformance,
} from "../api/dashboard";
import { exportDashboardExcel } from "../utils/dashboardExcel";

const NOT_INJECTED_LABEL = "미주입";
const INJECTED_LABEL = "주입완료";
const PRODUCTION_LABEL = "생산량";
const SUCCESS_LABEL = "성공";
const FAIL_LABEL = "실패";

const KEY_TYPE_LABEL: Record<number, string> = {
  10: "PRIVATE KEY",
  20: "DIK",
  30: "S/N",
  40: "MAC",
};

const STATUS_LABEL: Record<string, string> = {
  "1": "미주입",
  "2": "주입완료",
};

const WEEKDAY_LABEL = ["일", "월", "화", "수", "목", "금", "토"];

const EVENT_TYPE_LABEL: Record<string, string> = {
  PAGE_VIEW: "화면 진입",
  BUTTON_CLICK: "버튼 클릭",
  FORM_SUBMIT: "폼 제출",
  API_CALL: "API 호출",
  LOGIN: "로그인",
  LOGOUT: "로그아웃",
  ACCESS: "접근",
  ACCESS_DENIED: "접근 거부",
  CREATE: "생성",
  UPDATE: "수정",
  DELETE: "삭제",
  IMPORT: "업로드",
  EXPORT: "내보내기",
  CONNECT: "단말기 연결",
  INPUT: "입력",
  KEY_INJECT: "키 주입",
};

const DONUT_COLORS = [
  "indigo.6",
  "teal.6",
  "orange.6",
  "grape.6",
  "cyan.6",
  "lime.6",
  "pink.6",
  "yellow.6",
  "blue.6",
  "red.6",
];

const TERMINAL_COLOR_MAP: Record<string, string> = {
  WHITE: "#f8f9fa",
  BLACK: "#212529",
};

const VISIBLE_INJECTION_KEY_TYPES = new Set([30, 40]);

const INJECTION_CHART_SERIES = [
  { name: SUCCESS_LABEL, color: "teal.6" },
  { name: FAIL_LABEL, color: "red.6" },
] as const;

const TREND_DAY_LOOKBACK_MONTHS = 3;

const getTrendDateRange = (
  unit: TrendUnit,
): { startDate?: string; endDate?: string } => {
  if (unit !== "day") {
    return {};
  }
  const today = dayjs();
  return {
    startDate: today
      .subtract(TREND_DAY_LOOKBACK_MONTHS, "month")
      .format("YYYY-MM-DD"),
    endDate: today.format("YYYY-MM-DD"),
  };
};

const getHourlyDateRange = (
  unit: TrendUnit,
): { startDate: string; endDate: string } => {
  const today = dayjs();
  if (unit === "day") {
    const d = today.format("YYYY-MM-DD");
    return { startDate: d, endDate: d };
  }
  if (unit === "month") {
    return {
      startDate: today.startOf("month").format("YYYY-MM-DD"),
      endDate: today.endOf("month").format("YYYY-MM-DD"),
    };
  }
  return {
    startDate: today.startOf("year").format("YYYY-MM-DD"),
    endDate: today.endOf("year").format("YYYY-MM-DD"),
  };
};

const formatHourlyPeriodLabel = (
  unit: TrendUnit,
  startDate: string,
  endDate: string,
): string => {
  if (unit === "day") {
    return dayjs(startDate).format("YYYY-MM-DD");
  }
  if (unit === "month") {
    return dayjs(startDate).format("YYYY년 M월");
  }
  return dayjs(startDate).format("YYYY년");
};

const numberFormat = (value?: number) =>
  typeof value === "number" ? value.toLocaleString("ko-KR") : "-";

interface KpiCardProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  color: string;
  hint?: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ label, value, icon, color, hint }) => (
  <Card withBorder radius="md" padding="lg" shadow="sm">
    <Group justify="space-between" align="flex-start" wrap="nowrap">
      <Box>
        <Text size="xs" c="dimmed" fw={600} tt="uppercase">
          {label}
        </Text>
        <Text fz={28} fw={700} mt={4} style={{ lineHeight: 1.1 }}>
          {value}
        </Text>
        {hint && (
          <Text size="xs" c="dimmed" mt={6}>
            {hint}
          </Text>
        )}
      </Box>
      <Box c={color} style={{ flexShrink: 0 }}>
        {icon}
      </Box>
    </Group>
  </Card>
);

const DashBoard: React.FC<{ menuTitle: string }> = ({ menuTitle }) => {
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [modelStatus, setModelStatus] = useState<ModelStatus[]>([]);
  const [trend, setTrend] = useState<ProductionTrendPoint[]>([]);
  const [trendUnit, setTrendUnit] = useState<TrendUnit>("day");
  const [hourlyUnit, setHourlyUnit] = useState<TrendUnit>("day");
  const [hourlyDateRange, setHourlyDateRange] = useState(() =>
    getHourlyDateRange("day"),
  );
  const [vanLabelMap, setVanLabelMap] = useState<Record<string, string>>({});
  const [exporting, setExporting] = useState(false);
  const [hourly, setHourly] = useState<HourlyProductionPoint[]>([]);
  const [injectionResult, setInjectionResult] = useState<InjectionResult[]>([]);
  const [distribution, setDistribution] = useState<DistributionPoint[]>([]);
  const [distDimension, setDistDimension] =
    useState<DistributionDimension>("van");
  const [workerPerf, setWorkerPerf] = useState<WorkerPerformance[]>([]);
  const [heatmap, setHeatmap] = useState<WeekdayHourCount[]>([]);
  const [audit, setAudit] = useState<AuditSummary | null>(null);

  const loadTrend = useCallback(async (unit: TrendUnit) => {
    const data = await fetchProductionTrend({ unit, ...getTrendDateRange(unit) });
    setTrend(data);
  }, []);

  const loadHourly = useCallback(async (unit: TrendUnit) => {
    const range = getHourlyDateRange(unit);
    const data = await fetchHourlyProduction(range);
    setHourly(data);
    setHourlyDateRange(range);
  }, []);

  const loadDistribution = useCallback(
    async (dimension: DistributionDimension) => {
      const data = await fetchDistribution(dimension);
      setDistribution(data);
    },
    [],
  );

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [
        summaryRes,
        modelRes,
        vanRes,
        hourlyRes,
        injResRes,
        workerRes,
        heatmapRes,
        auditRes,
      ] = await Promise.all([
        fetchDashboardSummary(),
        fetchModelStatus(),
        fetchVanCodes(),
        fetchHourlyProduction(getHourlyDateRange(hourlyUnit)),
        fetchInjectionResult(),
        fetchWorkerPerformance(),
        fetchWeekdayHourHeatmap(),
        fetchAuditSummary(),
      ]);
      setSummary(summaryRes);
      setModelStatus(modelRes);
      setVanLabelMap(
        Object.fromEntries(vanRes.map((v) => [v.code, v.name])),
      );
      setHourly(hourlyRes);
      setHourlyDateRange(getHourlyDateRange(hourlyUnit));
      setInjectionResult(injResRes);
      setWorkerPerf(workerRes);
      setHeatmap(heatmapRes);
      setAudit(auditRes);
      await Promise.all([loadTrend(trendUnit), loadDistribution(distDimension)]);
    } catch (err) {
      console.error("대시보드 데이터 로드 실패", err);
      toast.error("대시보드 데이터를 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [loadTrend, loadDistribution, trendUnit, distDimension, hourlyUnit]);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTrendUnitChange = async (value: string) => {
    const unit = value as TrendUnit;
    setTrendUnit(unit);
    try {
      await loadTrend(unit);
    } catch (err) {
      console.error("생산량 추이 로드 실패", err);
      toast.error("생산량 추이를 불러오지 못했습니다.");
    }
  };

  const handleHourlyUnitChange = async (value: string) => {
    const unit = value as TrendUnit;
    setHourlyUnit(unit);
    try {
      await loadHourly(unit);
    } catch (err) {
      console.error("시간대별 생산량 로드 실패", err);
      toast.error("시간대별 생산량을 불러오지 못했습니다.");
    }
  };

  const handleDistDimensionChange = async (value: string) => {
    const dimension = value as DistributionDimension;
    setDistDimension(dimension);
    try {
      await loadDistribution(dimension);
    } catch (err) {
      console.error("분포 데이터 로드 실패", err);
      toast.error("분포 데이터를 불러오지 못했습니다.");
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportDashboardExcel({
        summary,
        modelStatus,
        trend,
        trendUnit,
        vanLabelMap,
      });
      toast.success("엑셀 리포트를 내려받았습니다.");
    } catch (err) {
      console.error("엑셀 내보내기 실패", err);
      toast.error("엑셀 내보내기에 실패했습니다.");
    } finally {
      setExporting(false);
    }
  };

  const modelChartData = useMemo(
    () =>
      modelStatus.map((m) => ({
        model: m.externalModelName || m.internalModelName || `#${m.terminalModelId}`,
        [NOT_INJECTED_LABEL]: m.notInjectedCount,
        [INJECTED_LABEL]: m.injectedCount,
      })),
    [modelStatus],
  );

  const trendChartData = useMemo(
    () =>
      trend.map((t) => ({
        period: t.period,
        [PRODUCTION_LABEL]: t.count,
      })),
    [trend],
  );

  const hourlyChartData = useMemo(() => {
    const byHour = new Map(hourly.map((h) => [h.hour, h.count]));
    return Array.from({ length: 24 }, (_, hour) => ({
      hour: `${String(hour).padStart(2, "0")}시`,
      [PRODUCTION_LABEL]: byHour.get(hour) ?? 0,
    }));
  }, [hourly]);

  const hasHourlyData = useMemo(
    () => hourly.some((h) => h.count > 0),
    [hourly],
  );

  const filteredInjectionResult = useMemo(
    () =>
      injectionResult.filter((r) => VISIBLE_INJECTION_KEY_TYPES.has(r.keyType)),
    [injectionResult],
  );

  const injectionChartData = useMemo(
    () =>
      filteredInjectionResult.map((r) => ({
        item: KEY_TYPE_LABEL[r.keyType] ?? `#${r.keyType}`,
        [SUCCESS_LABEL]: r.successCount,
        [FAIL_LABEL]: r.failCount,
      })),
    [filteredInjectionResult],
  );

  const distLabel = useCallback(
    (code: string) => {
      if (distDimension === "van") {
        return vanLabelMap[code] ?? code;
      }
      if (distDimension === "status") {
        return STATUS_LABEL[code] ?? code;
      }
      return code;
    },
    [distDimension, vanLabelMap],
  );

  const distributionChartData = useMemo(
    () =>
      distribution.map((d, idx) => ({
        name: distLabel(d.code),
        value: d.count,
        color:
          distDimension === "color"
            ? (TERMINAL_COLOR_MAP[d.code.toUpperCase()] ??
              DONUT_COLORS[idx % DONUT_COLORS.length])
            : DONUT_COLORS[idx % DONUT_COLORS.length],
      })),
    [distribution, distLabel, distDimension],
  );

  const hasWhiteDonutSegment = useMemo(
    () =>
      distDimension === "color" &&
      distribution.some((d) => d.code.toUpperCase() === "WHITE"),
    [distDimension, distribution],
  );

  const hourlyPeriodLabel = formatHourlyPeriodLabel(
    hourlyUnit,
    hourlyDateRange.startDate,
    hourlyDateRange.endDate,
  );

  const distributionTotal = useMemo(
    () => distribution.reduce((acc, d) => acc + d.count, 0),
    [distribution],
  );

  const heatmapMax = useMemo(
    () => heatmap.reduce((acc, h) => Math.max(acc, h.count), 0),
    [heatmap],
  );

  const heatmapMatrix = useMemo(() => {
    const map = new Map<string, number>();
    heatmap.forEach((h) => map.set(`${h.weekday}-${h.hour}`, h.count));
    return WEEKDAY_LABEL.map((label, idx) => ({
      weekday: label,
      cells: Array.from({ length: 24 }, (_, hour) => ({
        hour,
        count: map.get(`${idx + 1}-${hour}`) ?? 0,
      })),
    }));
  }, [heatmap]);

  const hasHeatmapData = heatmapMax > 0;

  const eventChartData = useMemo(
    () =>
      (audit?.eventCounts ?? []).map((e) => ({
        event: EVENT_TYPE_LABEL[e.code] ?? e.code,
        건수: e.count,
      })),
    [audit],
  );

  const completionRate = summary?.completionRate ?? 0;

  return (
    <Box pos="relative" style={{ height: "100%", overflowY: "auto" }}>
      <LoadingOverlay visible={loading} zIndex={50} />

      <Stack gap="lg" p="md">
        {/* 헤더 */}
        <Group justify="space-between" align="center" wrap="wrap">
          <Title order={3}>{menuTitle || "대시보드"}</Title>
          <Group gap="xs">
            <Button
              variant="default"
              leftSection={<IconRefresh size={16} />}
              onClick={loadAll}
            >
              새로고침
            </Button>
            <Button
              leftSection={<IconDownload size={16} />}
              onClick={handleExport}
              loading={exporting}
              color="teal"
            >
              엑셀 리포트
            </Button>
          </Group>
        </Group>

        {/* KPI 카드 */}
        <SimpleGrid cols={{ base: 1, xs: 2, md: 3, lg: 5 }} spacing="md">
          <KpiCard
            label="전체 등록"
            value={numberFormat(summary?.totalCount)}
            icon={<IconDeviceMobile size={32} />}
            color="blue.6"
          />
          <KpiCard
            label="미주입"
            value={numberFormat(summary?.notInjectedCount)}
            icon={<IconHourglassEmpty size={32} />}
            color="gray.6"
            hint="남은 작업량"
          />
          <KpiCard
            label="주입완료(OK)"
            value={numberFormat(summary?.injectedCount)}
            icon={<IconCircleCheck size={32} />}
            color="teal.6"
          />
          <KpiCard
            label="오늘 주입 성공"
            value={numberFormat(summary?.todayInjectSuccess)}
            icon={<IconCircleCheck size={32} />}
            color="green.6"
            hint="금일 처리 건수"
          />
          <KpiCard
            label="오늘 주입 실패"
            value={numberFormat(summary?.todayInjectFail)}
            icon={<IconAlertTriangle size={32} />}
            color="red.6"
            hint="금일 에러 건수"
          />
        </SimpleGrid>

        {/* 완료율 + 모델별 현황 */}
        <Grid gutter="md">
          <Grid.Col span={{ base: 12, lg: 4 }}>
            <Card withBorder radius="md" padding="lg" shadow="sm" h="100%">
              <Text fw={600} mb="md">
                전체 완료율
              </Text>
              <Group justify="center">
                <RingProgress
                  size={180}
                  thickness={18}
                  roundCaps
                  sections={[{ value: completionRate, color: "teal" }]}
                  label={
                    <Text ta="center" fz={28} fw={700}>
                      {completionRate}%
                    </Text>
                  }
                />
              </Group>
              <Group justify="center" gap="xl" mt="md">
                <Box ta="center">
                  <Text size="xs" c="dimmed">
                    주입완료
                  </Text>
                  <Text fw={700} c="teal.7">
                    {numberFormat(summary?.injectedCount)}
                  </Text>
                </Box>
                <Box ta="center">
                  <Text size="xs" c="dimmed">
                    미주입
                  </Text>
                  <Text fw={700} c="gray.7">
                    {numberFormat(summary?.notInjectedCount)}
                  </Text>
                </Box>
              </Group>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, lg: 8 }}>
            <Card withBorder radius="md" padding="lg" shadow="sm" h="100%">
              <Text fw={600} mb="md">
                모델별 미주입 / 주입 현황
              </Text>
              {modelChartData.length > 0 ? (
                <BarChart
                  h={300}
                  data={modelChartData}
                  dataKey="model"
                  type="stacked"
                  withLegend
                  series={[
                    { name: NOT_INJECTED_LABEL, color: "gray.5" },
                    { name: INJECTED_LABEL, color: "teal.6" },
                  ]}
                />
              ) : (
                <EmptyState />
              )}
            </Card>
          </Grid.Col>
        </Grid>

        {/* 모델별 표 */}
        <Card withBorder radius="md" padding="lg" shadow="sm">
          <Text fw={600} mb="md">
            모델별 상세 (표)
          </Text>
          <Table.ScrollContainer minWidth={640}>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>내부모델</Table.Th>
                  <Table.Th>외부모델</Table.Th>
                  <Table.Th>VAN</Table.Th>
                  <Table.Th ta="right">총 수량</Table.Th>
                  <Table.Th ta="right">미주입</Table.Th>
                  <Table.Th ta="right">주입완료</Table.Th>
                  <Table.Th ta="right">완료율</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {modelStatus.length > 0 ? (
                  modelStatus.map((m) => {
                    const rate =
                      m.totalCount > 0
                        ? Math.round((m.injectedCount / m.totalCount) * 1000) /
                          10
                        : 0;
                    return (
                      <Table.Tr key={m.terminalModelId}>
                        <Table.Td>{m.internalModelName}</Table.Td>
                        <Table.Td>{m.externalModelName}</Table.Td>
                        <Table.Td>
                          {vanLabelMap[String(m.vanType)] ?? m.vanType}
                        </Table.Td>
                        <Table.Td ta="right">
                          {numberFormat(m.totalCount)}
                        </Table.Td>
                        <Table.Td ta="right">
                          {numberFormat(m.notInjectedCount)}
                        </Table.Td>
                        <Table.Td ta="right">
                          {numberFormat(m.injectedCount)}
                        </Table.Td>
                        <Table.Td ta="right">
                          <Badge
                            color={rate >= 100 ? "teal" : rate > 0 ? "blue" : "gray"}
                            variant="light"
                          >
                            {rate}%
                          </Badge>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })
                ) : (
                  <Table.Tr>
                    <Table.Td colSpan={7}>
                      <EmptyState />
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>

        {/* 생산량 추이 */}
        <Card withBorder radius="md" padding="lg" shadow="sm">
          <Group justify="space-between" mb="md" wrap="wrap">
            <Text fw={600}>기간별 생산량 추이 (주입 성공 건수)</Text>
            <SegmentedControl
              value={trendUnit}
              onChange={handleTrendUnitChange}
              data={[
                { label: "일별", value: "day" },
                { label: "월별", value: "month" },
                { label: "연별", value: "year" },
              ]}
            />
          </Group>
          {trendChartData.length > 0 ? (
            <LineChart
              h={320}
              data={trendChartData}
              dataKey="period"
              series={[{ name: PRODUCTION_LABEL, color: "blue.6" }]}
              curveType="natural"
              withDots
            />
          ) : (
            <EmptyState />
          )}
        </Card>

        {/* 시간대별 생산량 + 분포 도넛 */}
        <Grid gutter="md">
          <Grid.Col span={{ base: 12, lg: 7 }}>
            <Card withBorder radius="md" padding="lg" shadow="sm" h="100%">
              <Group justify="space-between" mb="md" wrap="wrap">
                <Box>
                  <Text fw={600}>시간대별 생산량 (주입 성공 건수)</Text>
                  <Text size="sm" c="dimmed" mt={4}>
                    조회 기간: {hourlyPeriodLabel}
                  </Text>
                </Box>
                <SegmentedControl
                  value={hourlyUnit}
                  onChange={handleHourlyUnitChange}
                  data={[
                    { label: "일별", value: "day" },
                    { label: "월별", value: "month" },
                    { label: "연별", value: "year" },
                  ]}
                />
              </Group>
              {hasHourlyData ? (
                <BarChart
                  h={300}
                  data={hourlyChartData}
                  dataKey="hour"
                  series={[{ name: PRODUCTION_LABEL, color: "indigo.6" }]}
                />
              ) : (
                <EmptyState />
              )}
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, lg: 5 }}>
            <Card withBorder radius="md" padding="lg" shadow="sm" h="100%">
              <Group justify="space-between" mb="md" wrap="wrap">
                <Text fw={600}>단말기 분포</Text>
                <SegmentedControl
                  size="xs"
                  value={distDimension}
                  onChange={handleDistDimensionChange}
                  data={[
                    { label: "VAN", value: "van" },
                    { label: "상태", value: "status" },
                    { label: "색상", value: "color" },
                  ]}
                />
              </Group>
              {distributionTotal > 0 ? (
                <Group justify="center">
                  <DonutChart
                    h={260}
                    data={distributionChartData}
                    withLabelsLine
                    withLabels
                    chartLabel={`총 ${numberFormat(distributionTotal)}`}
                    tooltipDataSource="segment"
                    strokeColor={hasWhiteDonutSegment ? "gray.4" : undefined}
                    strokeWidth={hasWhiteDonutSegment ? 2 : 1}
                  />
                </Group>
              ) : (
                <EmptyState />
              )}
            </Card>
          </Grid.Col>
        </Grid>

        {/* 주입 항목별 성공/실패 (품질) */}
        <Card withBorder radius="md" padding="lg" shadow="sm">
          <Text fw={600} mb="md">
            주입 항목별 성공 / 실패 (품질)
          </Text>
          {injectionChartData.length > 0 ? (
            <Grid gutter="md">
              <Grid.Col span={{ base: 12, md: 6 }}>
                <BarChart
                  h={220}
                  data={injectionChartData}
                  dataKey="item"
                  type="stacked"
                  orientation="vertical"
                  withLegend
                  series={[...INJECTION_CHART_SERIES]}
                  tooltipProps={{
                    content: ({ label, payload }) => (
                      <ChartTooltip
                        label={String(
                          payload?.[0]?.payload?.item ?? label ?? "",
                        )}
                        payload={payload}
                        series={[...INJECTION_CHART_SERIES]}
                      />
                    ),
                  }}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <Table.ScrollContainer minWidth={320}>
                  <Table striped highlightOnHover withTableBorder>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>주입 항목</Table.Th>
                        <Table.Th ta="right">전체</Table.Th>
                        <Table.Th ta="right">성공</Table.Th>
                        <Table.Th ta="right">실패</Table.Th>
                        <Table.Th ta="right">실패율</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {filteredInjectionResult.map((r) => (
                        <Table.Tr key={r.keyType}>
                          <Table.Td>
                            {KEY_TYPE_LABEL[r.keyType] ?? `#${r.keyType}`}
                          </Table.Td>
                          <Table.Td ta="right">
                            {numberFormat(r.totalCount)}
                          </Table.Td>
                          <Table.Td ta="right">
                            {numberFormat(r.successCount)}
                          </Table.Td>
                          <Table.Td ta="right">
                            {numberFormat(r.failCount)}
                          </Table.Td>
                          <Table.Td ta="right">
                            <Badge
                              color={
                                r.failRate <= 0
                                  ? "teal"
                                  : r.failRate < 10
                                    ? "yellow"
                                    : "red"
                              }
                              variant="light"
                            >
                              {r.failRate}%
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Table.ScrollContainer>
              </Grid.Col>
            </Grid>
          ) : (
            <EmptyState />
          )}
        </Card>

        {/* 작업자별 생산 성과 + 요일×시간 히트맵 (Phase 3) */}
        <Grid gutter="md">
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card withBorder radius="md" padding="lg" shadow="sm" h="100%">
              <Text fw={600} mb="md">
                작업자별 생산 성과 (키주입)
              </Text>
              <Table.ScrollContainer minWidth={280}>
                <Table striped highlightOnHover withTableBorder>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>작업자</Table.Th>
                      <Table.Th ta="right">처리량</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {workerPerf.length > 0 ? (
                      workerPerf.map((w) => (
                        <Table.Tr key={w.userNo}>
                          <Table.Td>{w.userName}</Table.Td>
                          <Table.Td ta="right">
                            {numberFormat(w.totalCount)}
                          </Table.Td>
                        </Table.Tr>
                      ))
                    ) : (
                      <Table.Tr>
                        <Table.Td colSpan={2}>
                          <EmptyState />
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card withBorder radius="md" padding="lg" shadow="sm" h="100%">
              <Text fw={600} mb="md">
                요일 × 시간 생산 히트맵 (주입 성공 건수)
              </Text>
              {hasHeatmapData ? (
                <Table.ScrollContainer minWidth={480}>
                  <Box>
                    <Group gap={2} mb={2} pl={32} wrap="nowrap">
                      {Array.from({ length: 24 }, (_, hour) => (
                        <Box
                          key={hour}
                          style={{
                            width: 26,
                            textAlign: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Text size="9px" c="dimmed">
                            {hour}
                          </Text>
                        </Box>
                      ))}
                    </Group>
                    {heatmapMatrix.map((row) => (
                      <Group key={row.weekday} gap={2} mb={2} wrap="nowrap">
                        <Box style={{ width: 30, flexShrink: 0 }}>
                          <Text size="xs" fw={600}>
                            {row.weekday}
                          </Text>
                        </Box>
                        {row.cells.map((cell) => (
                          <HeatCell
                            key={cell.hour}
                            count={cell.count}
                            max={heatmapMax}
                            label={`${row.weekday} ${cell.hour}시`}
                          />
                        ))}
                      </Group>
                    ))}
                  </Box>
                </Table.ScrollContainer>
              ) : (
                <EmptyState />
              )}
            </Card>
          </Grid.Col>
        </Grid>

        {/* 운영/감사 (Phase 3) */}
        <Grid gutter="md">
          <Grid.Col span={{ base: 12, lg: 5 }}>
            <Stack gap="md" h="100%">
              <SimpleGrid cols={{ base: 1, xs: 3, lg: 1 }} spacing="md">
                <KpiCard
                  label="미승인 작업자"
                  value={numberFormat(audit?.pendingWorkerCount)}
                  icon={<IconUserExclamation size={32} />}
                  color="orange.6"
                  hint="승인 대기 인원"
                />
                <KpiCard
                  label="작업자 수"
                  value={numberFormat(audit?.workerCount)}
                  icon={<IconUsers size={32} />}
                  color="blue.6"
                />
                <KpiCard
                  label="활성 사용자"
                  value={numberFormat(audit?.activeUserCount)}
                  icon={<IconUsers size={32} />}
                  color="teal.6"
                  hint="재직 중"
                />
              </SimpleGrid>
            </Stack>
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 7 }}>
            <Card withBorder radius="md" padding="lg" shadow="sm" h="100%">
              <Text fw={600} mb="md">
                활동 유형별 건수 (운영/감사)
              </Text>
              {eventChartData.length > 0 ? (
                <BarChart
                  h={300}
                  data={eventChartData}
                  dataKey="event"
                  orientation="vertical"
                  series={[{ name: "건수", color: "grape.6" }]}
                />
              ) : (
                <EmptyState />
              )}
            </Card>
          </Grid.Col>
        </Grid>
      </Stack>
    </Box>
  );
};

interface HeatCellProps {
  count: number;
  max: number;
  label: string;
}

const HeatCell: React.FC<HeatCellProps> = ({ count, max, label }) => {
  const ratio = max > 0 ? count / max : 0;
  const bg =
    count === 0
      ? "var(--mantine-color-gray-1)"
      : `rgba(32, 138, 119, ${0.15 + ratio * 0.85})`;
  return (
    <Box
      title={`${label}: ${count}건`}
      style={{
        width: 26,
        height: 26,
        flexShrink: 0,
        borderRadius: 4,
        backgroundColor: bg,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {count > 0 && (
        <Text size="9px" c={ratio > 0.5 ? "white" : "dark"} fw={600}>
          {count}
        </Text>
      )}
    </Box>
  );
};

const EmptyState: React.FC = () => (
  <Group justify="center" py="xl">
    <Text c="dimmed" size="sm">
      표시할 데이터가 없습니다.
    </Text>
  </Group>
);

export default DashBoard;
